import { Download, Check, CircleCheck, Pencil, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { DynamicIcon } from "@/components/skills-marketplace/dynamic-icon";
import type { SkillListItem } from "@optidev/shared-types";

function formatCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

const CATEGORY_LABELS: Record<string, string> = {
  "creative-design": "Design",
  "document-processing": "Documents",
  "business-marketing": "Marketing",
  development: "Development",
  "web-development": "Web Dev",
  "enterprise-communication": "Communication",
  "workflow-automation": "Automation",
  security: "Security",
  productivity: "Productivity",
  database: "Database",
  analytics: "Analytics",
  "ai-research": "AI Research",
  media: "Media",
  video: "Video",
  scientific: "Scientific",
  custom: "Custom",
};

const CATEGORY_STYLES: Record<string, { badge: string; icon: string }> = {
  "creative-design":       { badge: "bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400",     icon: "bg-pink-100 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400" },
  "document-processing":   { badge: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400", icon: "bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400" },
  "business-marketing":    { badge: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",     icon: "bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400" },
  development:             { badge: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400", icon: "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400" },
  "web-development":       { badge: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400",     icon: "bg-cyan-100 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400" },
  "enterprise-communication": { badge: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400", icon: "bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400" },
  "workflow-automation":   { badge: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400", icon: "bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400" },
  security:                { badge: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",         icon: "bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400" },
  productivity:            { badge: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400", icon: "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400" },
  database:                { badge: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400", icon: "bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400" },
  analytics:               { badge: "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400",     icon: "bg-teal-100 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400" },
  "ai-research":           { badge: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400", icon: "bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400" },
  media:                   { badge: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",     icon: "bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400" },
  video:                   { badge: "bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-900/30 dark:text-fuchsia-400", icon: "bg-fuchsia-100 dark:bg-fuchsia-900/30 text-fuchsia-600 dark:text-fuchsia-400" },
  scientific:              { badge: "bg-lime-100 text-lime-700 dark:bg-lime-900/30 dark:text-lime-400",     icon: "bg-lime-100 dark:bg-lime-900/30 text-lime-600 dark:text-lime-400" },
};

const DEFAULT_STYLES = {
  badge: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400",
  icon: "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400",
};

interface SkillCardProps {
  skill: SkillListItem;
  isInstalled: boolean;
  isLoading: boolean;
  onInstall: () => void;
  onUninstall: () => void;
  onClick: () => void;
  isCustom?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
}

export function SkillCard({
  skill,
  isInstalled,
  isLoading,
  onInstall,
  onUninstall,
  onClick,
  isCustom = false,
  onEdit,
  onDelete,
}: SkillCardProps) {
  const CUSTOM_STYLES = {
    badge: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400",
    icon: "bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400",
  };
  const styles = isCustom ? CUSTOM_STYLES : (CATEGORY_STYLES[skill.category] ?? DEFAULT_STYLES);
  const label = CATEGORY_LABELS[skill.category] ?? skill.category;

  return (
    <div
      className={cn(
        "group relative flex flex-col rounded-lg border border-border/50 dark:border-border/30 bg-card p-4 transition-colors cursor-pointer overflow-hidden",
        "hover:border-primary/30",
      )}
      onClick={onClick}
    >
      {/* Top-right: edit+delete (custom) or installed checkmark (marketplace) */}
      {isCustom ? (
        <div className="absolute top-2 right-2 flex items-center gap-0.5">
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7"
            onClick={(e) => {
              e.stopPropagation();
              onEdit?.();
            }}
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 text-destructive hover:text-destructive hover:bg-destructive/10"
                onClick={(e) => e.stopPropagation()}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Custom Skill</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete &ldquo;{skill.name}&rdquo;? This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete?.();
                  }}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      ) : isInstalled ? (
        <div className="absolute top-3 right-3">
          <CircleCheck className="h-4 w-4 text-green-500" />
        </div>
      ) : null}

      <div className="flex items-start gap-3 mb-3">
        {/* Neutral icon */}
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted">
          <DynamicIcon name={skill.icon} className="h-4.5 w-4.5 text-muted-foreground" />
        </div>
        <div className="min-w-0 flex-1 pr-8">
          <h3 className="font-medium text-sm leading-tight truncate">
            {skill.name}
          </h3>
          <span className={cn("inline-block mt-1.5 px-2 py-0.5 rounded text-[10px] font-medium", styles.badge)}>
            {label}
          </span>
        </div>
      </div>

      <p className="text-xs text-muted-foreground line-clamp-2 mb-4 flex-1 leading-relaxed">
        {skill.description}
      </p>

      {isCustom ? null : (
        <div className="flex items-center justify-between">
          {skill.installCountExternal > 0 ? (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Download className="h-3 w-3" />
              <span>{formatCount(skill.installCountExternal)}</span>
            </div>
          ) : <div />}
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs px-3"
            disabled={isLoading}
            onClick={(e) => {
              e.stopPropagation();
              isInstalled ? onUninstall() : onInstall();
            }}
          >
            {isLoading ? (
              "..."
            ) : isInstalled ? (
              <span className="inline-flex items-center gap-1">
                <Check className="h-3 w-3" />
                Installed
              </span>
            ) : (
              "Install"
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
